import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { createMedicineDictionaryDetailUrl } from '@/entities/medicine-dictionary/util';
import usePopularMedicineDictionaryItems from '@/hooks/medicine-dictionary/usePopularMedicineDictionaryItems';
import { ROUTES } from '@/types/common';
import { SearchResult } from '@/types/search';
import { useRef, useState } from 'react';
import useGetKeywordIntegratedSearchMedicineDictionary, {
  EKeywordSearchMedicineDictionarySortType,
} from '../search/useGetKeywordIntegratedSearchMedicineDictionary';
import useCustomRouter from '../useCustomRouter';

export interface MedicineDictionarySearchViewModel {
  popularMedicineDictionaryItems?: MedicineDictionaryModel[];
  keywordSearchMedicineDictionary?: SearchResult<MedicineDictionaryModel>;
  isPopularMedicineDictionaryItemsLoading: boolean;
  keyword: string;
  searchInputRef: React.RefObject<HTMLInputElement>;
  currentPage: number;
  queries: { keyword: string; page: string };
  totalPage: number;
  totalCount: number;
  handleSubmit: () => void;
  handleResetSearchKeyword: () => void;
  handleBackButtonClick: () => void;
  onClickMedicineDictionary: (medicineDictionary: MedicineDictionaryModel) => () => void;
  handleChangeSearchKeyword: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const useMedicineDictionarySearchViewModel = (): MedicineDictionarySearchViewModel => {
  const { router } = useCustomRouter();
  const queries = router.query as { keyword: string; page: string };

  const currentPage = Number(queries.page || '1');
  const [keyword, setKeyword] = useState(queries.keyword || '');

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { popularMedicineDictionaryItems, isPopularMedicineDictionaryItemsLoading } =
    usePopularMedicineDictionaryItems();
  const { keywordSearchMedicineDictionary, totalPage, totalCount } = useGetKeywordIntegratedSearchMedicineDictionary({
    keyword: queries.keyword || '',
    enabled: !!queries.keyword,
    limit: 10,
    page: currentPage,
    sortType: EKeywordSearchMedicineDictionarySortType.Score,
  });

  const handleSubmit = () => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      router.replace({
        pathname: ROUTES.MEDICINE_DICTIONARY.SEARCH.path,
        query: { keyword: searchKeyword },
      });
    }
  };

  const handleResetSearchKeyword = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    router.push({
      pathname: ROUTES.MEDICINE_DICTIONARY.HOME.path,
      query: {},
    });
  };

  const handleBackButtonClick = () => {
    router.back();
  };

  const onClickMedicineDictionary = (medicineDictionary: MedicineDictionaryModel) => () => {
    router.push(createMedicineDictionaryDetailUrl(medicineDictionary));
  };

  const handleChangeSearchKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  return {
    popularMedicineDictionaryItems,
    isPopularMedicineDictionaryItemsLoading,
    keyword,
    keywordSearchMedicineDictionary,
    searchInputRef,
    currentPage,
    queries,
    totalPage,
    totalCount,
    handleSubmit,
    handleResetSearchKeyword,
    handleBackButtonClick,
    onClickMedicineDictionary,
    handleChangeSearchKeyword,
  };
};

export default useMedicineDictionarySearchViewModel;
